<template>
  <div class="p-6">
    <div class="mb-4">
      <h3 class="text-lg font-semibold mb-2 flex items-center gap-2">
        <i class="pi pi-book" style="color: var(--primary-gold);"></i>
        <span style="color: var(--text-primary);">Standard Operating Procedures</span>
      </h3>
      <p class="text-sm" style="color: var(--text-secondary);">
        SOPs that operationalize this process domain. Select one to view its full procedure.
      </p>
    </div>

    <!-- SOP cards -->
    <div v-if="sops.length > 0" class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div
        v-for="sop in sops"
        :key="sop.sop_id"
        class="sop-card p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md"
        style="background: var(--background-primary); border-color: var(--border-light);"
        @click="openSop(sop.sop_id)"
      >
        <div class="flex items-start gap-3">
          <i class="pi pi-book text-xl" style="color: var(--primary-gold);"></i>
          <div class="flex-1 min-w-0">
            <h4 class="font-semibold mb-1" style="color: var(--text-primary);">{{ sop.sop_id }}</h4>
            <p class="text-sm" style="color: var(--text-secondary);">{{ sop.name }}</p>
            <p class="text-xs mt-2" style="color: var(--text-tertiary);">
              <span v-if="sop.linked_practices?.length">{{ sop.linked_practices.join(', ') }}</span>
              <span v-if="sop.linked_practices?.length && sop.duration"> · </span>
              <span v-if="sop.duration">{{ humanize(sop.duration) }}</span>
              <span v-if="sop.team_size"> · {{ humanize(sop.team_size) }}</span>
            </p>
            <p v-if="sop.process_output" class="text-xs mt-1" style="color: var(--text-tertiary);">
              → {{ sop.process_output }}
            </p>
          </div>
          <i class="pi pi-arrow-right" style="color: var(--text-muted);"></i>
        </div>
      </div>
    </div>

    <!-- Empty / unavailable -->
    <div v-else class="text-center py-12">
      <i class="pi pi-book text-4xl mb-4" style="color: var(--text-muted);"></i>
      <p style="color: var(--text-secondary);">No SOPs are available for this process domain yet.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { Section } from '@/types/ashmatics';
import { useCharModel } from '@/composables/useCharModel';
import { useExplorerStore } from '@/stores/explorerStore';

const props = defineProps<{
  section: Section | null;
}>();

const { processDomainFiles } = useCharModel();
const explorerStore = useExplorerStore();

// The SOP list comes from the staged process-domain JSON (framework source of
// truth); only the SOP body is fetched from the KB, on open. For a process item
// the section id is its domain code (e.g. "RM", "SA").
const sops = computed(() => {
  const code = props.section?.domain_code || props.section?.id;
  return code ? processDomainFiles[code]?.standard_operating_procedures ?? [] : [];
});

const openSop = (slug: string) => {
  explorerStore.openSopArtifact(slug);
};

const humanize = (s: string) => s.replace(/_/g, ' ');
</script>

<style scoped>
.sop-card:hover {
  border-color: var(--primary-gold);
}
</style>
